import { useEffect, useState } from "react";
import { getEmailPreferences, updateEmailPreferences } from "../services/emailPreferenceService";
import { getUserToken } from "../utils/tokenUtils";
import { getApiErrorMessage } from "../utils/Util";
import { useLanguage } from "../context/LanguageContext";

const defaultPrefs = {
  orderConfirmation: true,
  orderStatusUpdates: true,
  promotionalEmails: true,
  newsletter: false,
  ticketUpdates: true,
  securityAlerts: true,
  feedbackRequests: true,
};

function EmailPreferencesPopup() {
  const { t } = useLanguage();
  const token = getUserToken();
  const [prefs, setPrefs] = useState(defaultPrefs);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const prefFields: { key: keyof typeof defaultPrefs; label: string }[] = [
    { key: "orderConfirmation",   label: t("emailPreferences.orderConfirmation") },
    { key: "orderStatusUpdates",  label: t("emailPreferences.orderStatusUpdates") },
    { key: "promotionalEmails",   label: t("emailPreferences.promotionalEmails") },
    { key: "newsletter",          label: t("emailPreferences.newsletter") },
    { key: "ticketUpdates",       label: t("emailPreferences.ticketUpdates") },
    { key: "securityAlerts",      label: t("emailPreferences.securityAlerts") },
    { key: "feedbackRequests",    label: t("emailPreferences.feedbackRequests") },
  ];

  useEffect(() => {
    const modalEl = document.getElementById("emailPreferencesModal");
    if (!modalEl) return;

    const handleShow = () => {
      setLoading(true);
      setError("");
      setSuccess(false);
      getEmailPreferences()
        .then((res) => {
          const p = res.data;
          setPrefs({
            orderConfirmation: p.orderConfirmation,
            orderStatusUpdates: p.orderStatusUpdates,
            promotionalEmails: p.promotionalEmails,
            newsletter: p.newsletter,
            ticketUpdates: p.ticketUpdates,
            securityAlerts: p.securityAlerts,
            feedbackRequests: p.feedbackRequests,
          });
        })
        .catch(() => setError(t("emailPreferences.loadError")))
        .finally(() => setLoading(false));
    };

    modalEl.addEventListener("show.bs.modal", handleShow);
    return () => modalEl.removeEventListener("show.bs.modal", handleShow);
  }, []);

  const handleSave = async () => {
    if (!token) return;
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      const res = await updateEmailPreferences({ customerId: token.userId, ...prefs });
      const p = res.data;
      setPrefs({
        orderConfirmation: p.orderConfirmation,
        orderStatusUpdates: p.orderStatusUpdates,
        promotionalEmails: p.promotionalEmails,
        newsletter: p.newsletter,
        ticketUpdates: p.ticketUpdates,
        securityAlerts: p.securityAlerts,
        feedbackRequests: p.feedbackRequests,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(getApiErrorMessage(err, t("emailPreferences.saveError")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal fade" id="emailPreferencesModal" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content rounded-4 shadow">
          <div className="modal-header border-0 pb-0">
            <div className="d-flex align-items-center gap-2">
              <i className="bi bi-envelope-at-fill text-danger fs-5"></i>
              <h6 className="fw-bold mb-0">{t("emailPreferences.title")}</h6>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label={t("common.close")} />
          </div>

          <div className="modal-body px-4 pb-2">
            {loading ? (
              Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="d-flex justify-content-between align-items-center mb-3">
                  <div className="skeleton" style={{ height: "14px", width: "55%" }} />
                  <div className="skeleton" style={{ height: "22px", width: "44px", borderRadius: "11px" }} />
                </div>
              ))
            ) : error && !prefs ? (
              <div className="alert alert-danger py-2 small">{error}</div>
            ) : (
              prefFields.map(({ key, label }) => (
                <div key={key} className="d-flex justify-content-between align-items-center mb-3">
                  <label className="form-check-label small fw-semibold" htmlFor={`pref-popup-${key}`}>
                    {label}
                  </label>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id={`pref-popup-${key}`}
                      checked={prefs[key]}
                      onChange={(e) => setPrefs((prev) => ({ ...prev, [key]: e.target.checked }))}
                    />
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="modal-footer flex-column gap-2 border-0 pt-0">
            {success && (
              <div className="alert alert-success py-2 small w-100 mb-0 text-center">
                <i className="bi bi-check-circle me-1"></i>
                {t("emailPreferences.saveSuccess")}
              </div>
            )}
            {error && !loading && (
              <div className="alert alert-danger py-2 small w-100 mb-0 text-center">
                <i className="bi bi-x-circle me-1"></i>
                {error}
              </div>
            )}
            <button
              type="button"
              className="btn btn-danger w-100 rounded-pill"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving && <span className="spinner-border spinner-border-sm me-1" />}
              {t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmailPreferencesPopup;
